import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Button, Chip, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip, TextField,
  MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Alert, CircularProgress,
  InputAdornment, LinearProgress,
} from '@mui/material';
import { Add, Edit, Delete, Refresh, Search, CheckCircle, Schedule } from '@mui/icons-material';
import { projectService, ProjectTask, Project } from '../../services/projectService';

const statusColors: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  TODO: 'default', IN_PROGRESS: 'primary', ON_HOLD: 'warning', COMPLETED: 'success', CANCELLED: 'error',
};
const priorityColors: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  LOW: 'default', MEDIUM: 'info', HIGH: 'warning', CRITICAL: 'error',
};

const TasksPage: React.FC = () => {
  const [tasks, setTasks] = useState<ProjectTask[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<ProjectTask | null>(null);
  const [formData, setFormData] = useState({
    projectId: '', title: '', description: '', priority: 'MEDIUM', status: 'TODO',
    dueDate: '', estimatedHours: '',
  });
  const [formLoading, setFormLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [tasksData, projectsData] = await Promise.all([
        projectService.getAllTasks({
          status: filterStatus || undefined,
          projectId: filterProject ? parseInt(filterProject) : undefined,
        }),
        projectService.getProjects(),
      ]);
      setTasks(tasksData.filter(t => !searchTerm || t.title.toLowerCase().includes(searchTerm.toLowerCase())));
      setProjects(projectsData);
    } catch (err: any) {
      setError(err.message || 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterProject, searchTerm]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleOpenDialog = (task?: ProjectTask) => {
    if (task) {
      setEditingTask(task);
      setFormData({
        projectId: task.project_id.toString(),
        title: task.title,
        description: task.description || '',
        priority: task.priority,
        status: task.status,
        dueDate: task.due_date?.split('T')[0] || '',
        estimatedHours: task.estimated_hours?.toString() || '',
      });
    } else {
      setEditingTask(null);
      setFormData({ projectId: '', title: '', description: '', priority: 'MEDIUM', status: 'TODO', dueDate: '', estimatedHours: '' });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.title.trim() || !formData.projectId) {
      setError('Title and project are required');
      return;
    }
    setFormLoading(true);
    try {
      const data = {
        title: formData.title, description: formData.description || undefined,
        priority: formData.priority, status: formData.status,
        dueDate: formData.dueDate || undefined,
        estimatedHours: formData.estimatedHours ? parseFloat(formData.estimatedHours) : undefined,
      };
      if (editingTask) {
        await projectService.updateTask(editingTask.id, data);
      } else {
        await projectService.createTask(parseInt(formData.projectId), data);
      }
      setDialogOpen(false);
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to save task');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await projectService.deleteTask(id);
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to delete task');
    }
  };

  const todoCount = tasks.filter(t => t.status === 'TODO').length;
  const inProgressCount = tasks.filter(t => t.status === 'IN_PROGRESS').length;
  const completedCount = tasks.filter(t => t.status === 'COMPLETED').length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>Task Management</Typography>
          <Typography variant="body2" color="text.secondary">Manage tasks across all projects</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh"><IconButton onClick={fetchData}><Refresh /></IconButton></Tooltip>
          <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>New Task</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 4 }}>
          <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h4" color="text.secondary">{todoCount}</Typography>
            <Typography variant="caption">To Do</Typography>
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 4 }}>
          <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h4" color="primary">{inProgressCount}</Typography>
            <Typography variant="caption">In Progress</Typography>
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 4 }}>
          <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h4" color="success.main">{completedCount}</Typography>
            <Typography variant="caption">Completed</Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField fullWidth size="small" placeholder="Search..." value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField select fullWidth size="small" label="Project" value={filterProject}
              onChange={(e) => setFilterProject(e.target.value)}>
              <MenuItem value="">All Projects</MenuItem>
              {projects.map(p => <MenuItem key={p.id} value={p.id.toString()}>{p.project_name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField select fullWidth size="small" label="Status" value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="TODO">To Do</MenuItem>
              <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
              <MenuItem value="COMPLETED">Completed</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <Typography variant="body2" color="text.secondary">{tasks.length} tasks</Typography>
          </Grid>
        </Grid>
      </Paper>

      {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box> : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.50' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Task</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Project</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Priority</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Due Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Progress</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tasks.length === 0 ? (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No tasks found</Typography>
                  </TableCell></TableRow>
                ) : tasks.map(task => (
                  <TableRow key={task.id} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                    <TableCell>
                      <Typography fontWeight="medium">{task.title}</Typography>
                      <Typography variant="caption" color="text.secondary">{task.task_code}</Typography>
                    </TableCell>
                    <TableCell>{task.project_name || '-'}</TableCell>
                    <TableCell><Chip label={task.priority} size="small" color={priorityColors[task.priority]} /></TableCell>
                    <TableCell><Chip label={task.status.replace('_', ' ')} size="small" color={statusColors[task.status]} /></TableCell>
                    <TableCell>{task.due_date ? new Date(task.due_date).toLocaleDateString() : '-'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress variant="determinate" value={task.progress_percent || 0}
                          sx={{ width: 50, height: 6, borderRadius: 3 }} />
                        <Typography variant="caption">{task.progress_percent || 0}%</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => handleOpenDialog(task)}><Edit fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(task.id)}><Delete fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingTask ? 'Edit Task' : 'New Task'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <TextField select fullWidth label="Project" value={formData.projectId}
                onChange={(e) => setFormData({ ...formData, projectId: e.target.value })} required disabled={!!editingTask}>
                <MenuItem value="">Select Project</MenuItem>
                {projects.map(p => <MenuItem key={p.id} value={p.id.toString()}>{p.project_name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth label="Title" value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })} required />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth label="Description" value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })} multiline rows={2} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField select fullWidth label="Priority" value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}>
                <MenuItem value="LOW">Low</MenuItem>
                <MenuItem value="MEDIUM">Medium</MenuItem>
                <MenuItem value="HIGH">High</MenuItem>
                <MenuItem value="CRITICAL">Critical</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField select fullWidth label="Status" value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}>
                <MenuItem value="TODO">To Do</MenuItem>
                <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                <MenuItem value="ON_HOLD">On Hold</MenuItem>
                <MenuItem value="COMPLETED">Completed</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField fullWidth label="Due Date" type="date" value={formData.dueDate}
                onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField fullWidth label="Estimated Hours" type="number" value={formData.estimatedHours}
                onChange={(e) => setFormData({ ...formData, estimatedHours: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={formLoading}>{formLoading ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TasksPage;
